import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Calendar, Clock, MapPin, Baby, Filter } from "lucide-react";
import axios from "axios";
import { useBookingLinks, getBookingUrl } from "../context/BookingLinksContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ClassesPage = () => {
  const bookingLinks = useBookingLinks();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterDay, setFilterDay] = useState("all");

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      const allClasses = response.data || [];
      
      // Sort by day then time
      allClasses.sort((a, b) => {
        const dayDiff = DAYS_ORDER.indexOf(a.day_of_week) - DAYS_ORDER.indexOf(b.day_of_week);
        if (dayDiff !== 0) return dayDiff;
        return a.time.localeCompare(b.time);
      });
      
      setClasses(allClasses);
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setLoading(false);
    }
  };

  const shortenLocation = (loc) => loc.replace(/^Gymtots\s+/i, "");
  const locations = [...new Set(classes.map(c => c.location_display))];
  
  const filteredClasses = classes.filter(cls => {
    if (filterLocation !== "all" && cls.location_display !== filterLocation) return false;
    if (filterDay !== "all" && cls.day_of_week !== filterDay) return false;
    return true;
  });

  const groupedByDay = DAYS_ORDER.reduce((acc, day) => {
    const dayClasses = filteredClasses.filter(c => c.day_of_week === day);
    if (dayClasses.length > 0) {
      acc[day] = dayClasses;
    }
    return acc;
  }, {});

  const formatAgeRange = (minMonths, maxMonths) => {
    const formatMonths = (m) => {
      if (m < 12) return `${m}m`;
      const years = Math.floor(m / 12);
      const months = m % 12;
      return months > 0 ? `${years}y ${months}m` : `${years}y`;
    };
    return `${formatMonths(minMonths)} - ${formatMonths(maxMonths)}`;
  };

  return (
    <div className="min-h-screen" data-testid="classes-page">
      <Header />
      
      <main>
        {/* Hero */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-[#fff5f5] to-[#FFFBF5]">
          <div className="container-gymtots text-center">
            <h1 
              className="text-3xl md:text-4xl font-bold text-[#2D3436] mb-4"
              style={{ fontFamily: 'Fredoka, cursive' }}
            >
              Class <span className="text-[#ff3131]">Schedule</span>
            </h1>
            <p className="text-[#636E72] max-w-xl mx-auto">
              View our weekly class timetable. Find the perfect class for your little one!
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="py-6 bg-white border-b border-gray-100">
          <div className="container-gymtots">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#636E72]" />
                <span className="text-sm font-semibold text-[#2D3436]">Filter:</span>
              </div>
              
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="px-4 py-2 border-2 border-gray-100 rounded-full text-sm focus:border-[#7dbff8] outline-none"
              >
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc} value={loc}>{shortenLocation(loc)}</option>
                ))}
              </select>
              
              <select
                value={filterDay}
                onChange={(e) => setFilterDay(e.target.value)}
                className="px-4 py-2 border-2 border-gray-100 rounded-full text-sm focus:border-[#7dbff8] outline-none"
              >
                <option value="all">All Days</option>
                {DAYS_ORDER.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Schedule */}
        <section className="py-12 md:py-16 bg-[#FFFBF5]">
          <div className="container-gymtots">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-[#ff3131] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[#636E72]">Loading schedule...</p>
              </div>
            ) : classes.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl max-w-md mx-auto">
                <Calendar className="w-12 h-12 text-[#636E72] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#2D3436] mb-2" style={{ fontFamily: 'Fredoka, cursive' }}>
                  No Classes Yet
                </h3>
                <p className="text-[#636E72] text-sm">
                  Classes are being added. Check back soon or contact us!
                </p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-8">
                {Object.entries(groupedByDay).map(([day, dayClasses]) => (
                  <div key={day}>
                    <h2 
                      className="text-xl font-bold text-[#2D3436] mb-4 flex items-center gap-2"
                      style={{ fontFamily: 'Fredoka, cursive' }}
                    >
                      <Calendar className="w-5 h-5 text-[#ff3131]" />
                      {day}
                    </h2>
                    
                    <div className="space-y-3">
                      {dayClasses.map((cls) => (
                        <div 
                          key={cls.id}
                          className="bg-white rounded-xl p-4 border border-gray-100 hover:border-[#7dbff8] transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span 
                                  className="font-bold text-[#2D3436]"
                                  style={{ fontFamily: 'Fredoka, cursive' }}
                                >
                                  {cls.class_name}
                                </span>
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#7dbff8]/20 text-[#7dbff8]">
                                  £{cls.price.toFixed(2)}
                                </span>
                              </div>
                              
                              <div className="flex flex-wrap gap-3 text-xs text-[#636E72]">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5" />
                                  {cls.time} ({cls.duration_minutes} mins)
                                </span>
                                <span className="flex items-center gap-1">
                                  <Baby className="w-3.5 h-3.5" />
                                  {formatAgeRange(cls.min_age_months, cls.max_age_months)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {shortenLocation(cls.location_display)}
                                </span>
                              </div>
                            </div>
                            
                            <a
                              href={getBookingUrl(bookingLinks, cls.class_type)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-center px-4 py-2 bg-[#ff3131] hover:bg-[#e62e2e] text-white text-sm font-bold rounded-full transition-colors"
                              style={{ fontFamily: 'Fredoka, cursive' }}
                            >
                              Book a Trial Session!
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container-gymtots text-center max-w-2xl mx-auto">
            <h2 
              className="text-2xl font-bold text-[#2D3436] mb-4"
              style={{ fontFamily: 'Fredoka, cursive' }}
            >
              Not Sure Which <span className="text-[#7dbff8]">Class?</span>
            </h2>
            <p className="text-[#636E72] mb-6">
              Use our class finder to find the perfect class based on your child's age and your location.
            </p>
            <a
              href="/#class-finder"
              className="inline-block bg-[#ff3131] hover:bg-[#e62e2e] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors"
              style={{ fontFamily: 'Fredoka, cursive' }}
            >
              Find Your Class
            </a>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ClassesPage;
